/**
 * lenses.logic.ts
 * Pure analytical lens functions and telemetry math for Data Hall Floor Plan.
 * Standardized to ASHRAE TC 9.9 thermal envelopes, power density, and RU space capacity.
 */

export type TemperatureStatus = 'cold' | 'optimal' | 'warning' | 'hot' | 'critical';
export type PowerStatus = 'low' | 'moderate' | 'high' | 'overload' | 'empty';
export type SpaceStatus = 'free' | 'optimal' | 'dense' | 'full' | 'facility';
export type SpofStatus = 'spof' | 'redundant';

export interface LensMetricResult<TStatus = string> {
  fill: string;
  badge: string;
  status: TStatus;
  percent?: number;
}

export interface LegendStep {
  label: string;
  color: string;
  range: string;
}

export interface LensLegendConfig {
  title: string;
  icon: string;
  unit: string;
  standard: string;
  steps: LegendStep[];
}

export interface LensSummary {
  // Common
  activeLens: string;
  // Temperature
  avgValue?: string;
  maxValue?: string;
  alertCount?: number;
  // Power
  totalLoadKw?: string;
  totalCapacityKw?: string;
  avgPercent?: number;
  // Space
  totalUsedU?: number;
  totalCapacityU?: number;
  freeU?: number;
  // SPOF
  spofCount?: number;
  protectedCount?: number;
}

// ─── 1. Thermal Heatmap (ASHRAE TC 9.9) ──────────────────────────────────────
export function getTemperatureLens(tempC?: number | null): LensMetricResult<TemperatureStatus> {
  const t = tempC != null ? tempC : 20.0;
  const badge = `${t.toFixed(1)}°C`;

  if (t < 20.0) {
    return {
      fill: 'rgba(59, 130, 246, 0.45)', // Blue
      badge,
      status: 'cold',
    };
  }
  if (t <= 25.0) {
    return {
      fill: 'rgba(34, 197, 94, 0.45)', // Green (ASHRAE Recommended: 18-27°C, optimal 20-25°C)
      badge,
      status: 'optimal',
    };
  }
  if (t <= 30.0) {
    return {
      fill: 'rgba(234, 179, 8, 0.5)', // Yellow (ASHRAE Class A1 Allowable: 15-32°C)
      badge,
      status: 'warning',
    };
  }
  if (t <= 35.0) {
    return {
      fill: 'rgba(249, 115, 22, 0.6)', // Orange (Elevated)
      badge,
      status: 'hot',
    };
  }
  return {
    fill: 'rgba(239, 68, 68, 0.7)', // Red (Critical >35°C)
    badge,
    status: 'critical',
  };
}

// ─── 2. Power Density ────────────────────────────────────────────────────────
export function getPowerLens(loadKw?: number | null, capacityKw?: number | null): LensMetricResult<PowerStatus> {
  const load = Math.max(0, loadKw ?? 0);
  const cap = Math.max(0, capacityKw ?? 0);

  if (cap <= 0) {
    return {
      fill: 'rgba(100, 100, 100, 0.15)',
      badge: `${load.toFixed(1)} kW`,
      percent: 0,
      status: 'empty',
    };
  }

  const ratio = load / cap;
  const percent = Math.round(ratio * 100);
  const badge = `${load.toFixed(1)} kW`;

  if (ratio < 0.5) {
    return {
      fill: 'rgba(34, 197, 94, 0.4)', // Green
      badge,
      percent,
      status: 'low',
    };
  }
  if (ratio < 0.75) {
    return {
      fill: 'rgba(234, 179, 8, 0.45)', // Yellow
      badge,
      percent,
      status: 'moderate',
    };
  }
  if (ratio < 0.9) {
    return {
      fill: 'rgba(249, 115, 22, 0.55)', // Orange
      badge,
      percent,
      status: 'high',
    };
  }
  return {
    fill: 'rgba(239, 68, 68, 0.7)', // Red
    badge,
    percent,
    status: 'overload',
  };
}

// ─── 3. Space Capacity (RU Utilization) ──────────────────────────────────────
export function getSpaceCapacityLens(
  usedU?: number | null,
  totalU?: number | null,
  isFacility: boolean = false,
): LensMetricResult<SpaceStatus> {
  if (isFacility || totalU == null || totalU <= 0) {
    return {
      fill: 'rgba(100, 116, 139, 0.15)',
      badge: '0U',
      percent: 0,
      status: 'facility',
    };
  }

  const uUsed = Math.max(0, usedU ?? 0);
  const uTotal = Math.max(1, totalU);
  const ratio = uUsed / uTotal;
  const percent = Math.round(ratio * 100);
  const badge = `${uUsed}/${uTotal}U`;

  if (ratio < 0.5) {
    return {
      fill: 'rgba(34, 197, 94, 0.4)', // Green (<50%)
      badge,
      percent,
      status: 'free',
    };
  }
  if (ratio < 0.75) {
    return {
      fill: 'rgba(234, 179, 8, 0.45)', // Yellow (50-75%)
      badge,
      percent,
      status: 'optimal',
    };
  }
  if (ratio < 0.9) {
    return {
      fill: 'rgba(249, 115, 22, 0.55)', // Orange (75-90%)
      badge,
      percent,
      status: 'dense',
    };
  }
  return {
    fill: 'rgba(239, 68, 68, 0.7)', // Red (>=90%)
    badge,
    percent,
    status: 'full',
  };
}

// ─── 4. Single Point of Failure (SPOF) ───────────────────────────────────────
export function getSpofLens(hasSpof?: boolean | null, spofReason?: string | null): LensMetricResult<SpofStatus> {
  if (hasSpof) {
    const badge = spofReason ? spofReason : 'SPOF';
    return {
      fill: 'rgba(239, 68, 68, 0.65)',
      badge,
      status: 'spof',
    };
  }
  return {
    fill: 'rgba(34, 197, 94, 0.25)',
    badge: '2N',
    status: 'redundant',
  };
}

// ─── 5. Legend Configurations ────────────────────────────────────────────────
export function getLensLegendConfig(lens: string): LensLegendConfig {
  switch (lens) {
    case 'temperature':
      return {
        title: 'Тепловая карта',
        icon: 'thermometer',
        unit: '°C',
        standard: 'ASHRAE TC 9.9 Thermal Envelope',
        steps: [
          { label: 'Охлаждение', color: '#3b82f6', range: '< 20°C' },
          { label: 'Оптимум ASHRAE', color: '#22c55e', range: '20–25°C' },
          { label: 'Допустимо (Warning)', color: '#eab308', range: '25–30°C' },
          { label: 'Нагрев (Elevated)', color: '#f97316', range: '30–35°C' },
          { label: 'Перегрев (Critical)', color: '#ef4444', range: '> 35°C' },
        ],
      };
    case 'power':
      return {
        title: 'Плотность мощности',
        icon: 'zap',
        unit: 'кВт / %',
        standard: 'TIA-942 Electrical Feed Utilization',
        steps: [
          { label: 'Низкая нагрузка', color: '#22c55e', range: '< 50%' },
          { label: 'Номинальная', color: '#eab308', range: '50–75%' },
          { label: 'Высокая нагрузка', color: '#f97316', range: '75–90%' },
          { label: 'Перегрузка ввода', color: '#ef4444', range: '> 90%' },
        ],
      };
    case 'space':
      return {
        title: 'Заполненность юнитов (RU)',
        icon: 'package',
        unit: 'U / %',
        standard: 'White Space IT Capacity',
        steps: [
          { label: 'Свободно', color: '#22c55e', range: '< 50%' },
          { label: 'Оптимально', color: '#eab308', range: '50–75%' },
          { label: 'Высокая плотность', color: '#f97316', range: '75–90%' },
          { label: 'Заполнено', color: '#ef4444', range: '> 90%' },
          { label: 'Инфраструктура', color: '#64748b', range: '0U' },
        ],
      };
    case 'spof':
      return {
        title: 'Отказоустойчивость (SPOF)',
        icon: 'alert-triangle',
        unit: 'Статус',
        standard: 'Tier III/IV Redundancy (2N)',
        steps: [
          { label: 'Резервирован (2N / 2N+1)', color: '#22c55e', range: 'Защищено' },
          { label: 'Точка отказа (SPOF)', color: '#ef4444', range: 'Риск' },
        ],
      };
    default:
      return {
        title: 'Обычный вид',
        icon: 'eye',
        unit: '',
        standard: 'DCIM',
        steps: [],
      };
  }
}

// ─── 6. Summary Statistics ───────────────────────────────────────────────────
export function calculateLensSummary(
  lens: string,
  racks: Array<{ id: string; isFacility?: boolean; isBlocked?: boolean; rackType?: string; uCount?: number; [k: string]: any }>,
  telemetry: Record<string, any>,
): LensSummary {
  const summary: LensSummary = { activeLens: lens };
  const rackList = racks || [];
  if (rackList.length === 0) return summary;

  if (lens === 'temperature') {
    let sumTemp = 0;
    let maxTemp = -Infinity;
    let alertCount = 0;
    let count = 0;

    rackList.forEach(r => {
      const isPillar = Boolean(r.isBlocked || r.rackType === 'pillar');
      if (isPillar) return;

      const data = telemetry[r.id];
      const temp = data?.temperature ?? 20.0;
      sumTemp += temp;
      if (temp > maxTemp) maxTemp = temp;
      if (temp > 30.0) alertCount++;
      count++;
    });

    summary.avgValue = count > 0 ? `${(sumTemp / count).toFixed(1)}°C` : '—';
    summary.maxValue = maxTemp > -Infinity ? `${maxTemp.toFixed(1)}°C` : '—';
    summary.alertCount = alertCount;
  } else if (lens === 'power') {
    let totalLoad = 0;
    let totalCap = 0;

    rackList.forEach(r => {
      const isPillar = Boolean(r.isBlocked || r.rackType === 'pillar');
      if (isPillar) return;

      const data = telemetry[r.id];
      const load = data?.powerLoad ?? 0;
      const cap = data?.powerCapacity ?? 0;
      totalLoad += load;
      totalCap += cap;
    });

    summary.totalLoadKw = totalLoad.toFixed(1);
    summary.totalCapacityKw = totalCap.toFixed(1);
    summary.avgPercent = totalCap > 0 ? Math.round((totalLoad / totalCap) * 100) : 0;
  } else if (lens === 'space') {
    let totalUsed = 0;
    let totalCap = 0;

    rackList.forEach(r => {
      const isPillar = Boolean(r.isBlocked || r.rackType === 'pillar');
      const isFac = Boolean(r.isFacility || isPillar || (r.rackType ? ['crac', 'inrow_cooler', 'ups', 'pdu_frame'].includes(r.rackType) : false));
      if (!isFac) {
        const data = telemetry[r.id];
        const cap = data?.totalU ?? r.uCount ?? 42;
        const used = data?.usedU ?? 0;
        totalUsed += used;
        totalCap += cap;
      }
    });

    summary.totalUsedU = totalUsed;
    summary.totalCapacityU = totalCap;
    summary.freeU = Math.max(0, totalCap - totalUsed);
    summary.avgPercent = totalCap > 0 ? Math.round((totalUsed / totalCap) * 100) : 0;
  } else if (lens === 'spof') {
    let spofCount = 0;
    let protectedCount = 0;

    rackList.forEach(r => {
      const isPillar = Boolean(r.isBlocked || r.rackType === 'pillar');
      if (isPillar) return;

      const data = telemetry[r.id];
      if (data?.hasSpof) {
        spofCount++;
      } else {
        protectedCount++;
      }
    });

    summary.spofCount = spofCount;
    summary.protectedCount = protectedCount;
  }

  return summary;
}
